import { AcquisitionChannel, ALLOWED_TRACKING_PARAMS, TrackingParams } from '@water-web/types';

import { Analytics, IntelligentReminderPayload } from './types';
import { sessionKVStore } from '../persistence';

const getAcquisitionChannel = (
  utmSource?: string,
  gclid?: string,
  fbclid?: string,
  rwgToken?: string,
): AcquisitionChannel | null => {
  if (!utmSource && !gclid && !fbclid && !rwgToken) {
    return null;
  }

  if (rwgToken) {
    return 'Google';
  }

  if (utmSource === 'city-pages') {
    return 'SQUIRE';
  }

  // Facebook does not rewrite gclid from links, so this would be tracked by GA anyway
  if (gclid) {
    return 'Google Ads';
  }

  if (utmSource?.toLocaleLowerCase() === 'facebook') {
    return 'Meta Ads';
  }

  return null;
};

export const getIntelligentReminderPayload = (userId?: string): Record<string, any> | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const utmContent = urlParams.get('utm_content');
  const appointmentId = urlParams.get('appointment_id');

  // Check if current URL has intelligent reminder parameters
  const isValidSource = utmSource === 'intelligent-reminder' || utmSource === 'intelligent-reminders';
  const isValidMedium = utmMedium === 'email' || utmMedium === 'sms';
  const isValidContent = utmContent === 'book-again-started' || utmContent === 'book-again-finished';
  const isValidIntelligentReminder = isValidSource && isValidMedium && isValidContent && appointmentId;

  if (isValidIntelligentReminder) {
    const params: IntelligentReminderPayload = {
      utmMedium,
      appointmentId,
      userId,
    };

    // Store in session storage for later use
    sessionKVStore.setItem(
      'intelligent_reminder_data',
      JSON.stringify({
        utmMedium,
        appointmentId,
      }),
    );

    return params;
  }

  // If not in current URL, check session storage
  const storedData = sessionKVStore.getItem('intelligent_reminder_data');

  if (storedData) {
    try {
      const parsedData = JSON.parse(storedData);
      if (parsedData.utmMedium && parsedData.appointmentId) {
        const params: IntelligentReminderPayload = {
          utmMedium: parsedData.utmMedium,
          appointmentId: parsedData.appointmentId,
          userId,
        };
        return params;
      }
    } catch (e) {
      // Invalid JSON in storage, remove it
      sessionKVStore.removeItem('intelligent_reminder_data');
    }
  }

  return null;
};

export const clearIntelligentReminderData = (): void => {
  sessionKVStore.removeItem('intelligent_reminder_data');
};

export const trackIncomingVisitors = (analytics: Analytics): void => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get('utm_source');
  const gclid = urlParams.get('gclid');
  const fbclid = urlParams.get('fbclid');
  const rwgToken = urlParams.get('rwg_token');

  const urlAcqChannel = getAcquisitionChannel(utmSource, gclid, fbclid, rwgToken);
  const storedAcqChannel = sessionKVStore.getItem('acquisition-channel') as AcquisitionChannel;
  const acqChannel = urlAcqChannel || storedAcqChannel;

  if (acqChannel) {
    const utmMedium = urlParams.get('utm_medium');
    const isSquire = acqChannel === 'SQUIRE';
    const squireAcqChannelParams = {
      fromRolesPage: utmMedium === 'roles-page',
    };

    analytics.setAcquisitionChannel(acqChannel, isSquire ? squireAcqChannelParams : {});

    sessionKVStore.setItem('acquisition-channel', acqChannel);
  }
};

export const getApiTrackingParams = (): TrackingParams => {
  if (typeof window === 'undefined') {
    return {};
  }

  const trackingParams: TrackingParams = {};

  let urlParams = new URLSearchParams(window.location.search || '');

  const hasEnoughtUtmParams = (params: URLSearchParams) =>
    params.has('utm_source') || params.has('utm_medium') || params.has('utm_campaign');

  if (hasEnoughtUtmParams(urlParams)) {
    sessionKVStore.setItem('squire_search_string', urlParams.toString());
  } else {
    const paramsFromSessionStorage = sessionKVStore.getItem('squire_search_string') || '';
    urlParams = new URLSearchParams(paramsFromSessionStorage);
  }

  urlParams.forEach((value, key) => {
    if (ALLOWED_TRACKING_PARAMS.indexOf(key) !== -1) {
      trackingParams[key] = value;
    }
  });

  const timezone = Intl.DateTimeFormat()?.resolvedOptions()?.timeZone;
  if (timezone) {
    trackingParams.timezone = timezone;
  }

  return trackingParams;
};
